/**
 * Dashboard de Gestión de Usuarios
 * Permite ver, editar y desactivar/activar usuarios (eliminación lógica)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  DialogContentText,
  Tabs,
  Tab,
  Pagination,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled  } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8001/api';

// Styled Components para diseño responsivo
const MobileCard = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(145deg, #ffffff 0%, #f8fffe 50%, #f0f9f0 100%)',
  borderRadius: '16px',
  marginBottom: '16px',
  overflow: 'hidden',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(46, 90, 51, 0.15)',
  },
}));

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  background: 'linear-gradient(135deg, rgba(46, 90, 51, 0.03) 0%, rgba(61, 115, 66, 0.06) 100%)',
  borderBottom: '1px solid rgba(46, 90, 51, 0.08)',
  cursor: 'pointer',
});

const ExpandIcon = styled(Box)<{ expanded: boolean }>(({ expanded }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: expanded ? 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)' : 'rgba(46, 90, 51, 0.1)',
  color: expanded ? '#fff' : '#2e5a33',
  transition: 'all 0.3s ease',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const CardContent = styled(Box)<{ expanded: boolean }>(({ expanded }) => ({
  maxHeight: expanded ? '500px' : '0',
  opacity: expanded ? 1 : 0,
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: expanded ? '16px' : '0 16px',
}));

const DetailItem = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  margin: '8px 0',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #f8fffe 0%, #f0f9f0 100%)',
  border: '1px solid rgba(46, 90, 51, 0.06)',
});

const ActionsContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
  padding: '16px',
  marginTop: '8px',
  borderTop: '1px solid rgba(46, 90, 51, 0.1)',
  background: 'linear-gradient(135deg, rgba(46, 90, 51, 0.02) 0%, rgba(61, 115, 66, 0.04) 100%)',
  flexWrap: 'wrap',
});

interface Usuario {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  rol?: string;
  profile?: {
    rol?: string;
    es_admin?: boolean;
    es_cliente?: boolean;
    es_repartidor?: boolean;
    activo?: boolean;
  };
  fecha_registro?: string;
  esta_activo?: boolean;
}

export default function DashboardUsuarios() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPermanentDeleteDialog, setOpenPermanentDeleteDialog] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | null>(null);
  const [tabValue, setTabValue] = useState(0); // 0 = activos, 1 = inactivos
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  
  const [editForm, setEditForm] = useState({
    rol: '',
    username: '',
    email: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/dashboard/usuarios/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const usuariosData = Array.isArray(response.data) ? response.data : response.data.results || [];
      setUsuarios(usuariosData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showSnackbar('Error al cargar usuarios', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setEditForm({
      rol: getRolNombre(usuario),
      username: usuario.username,
      email: usuario.email,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUsuario(null);
    setEditForm({ rol: '', username: '', email: '' });
  };

  // Abrir diálogo de confirmación para desactivar/activar
  const handleOpenDeleteDialog = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedUsuario(null);
  };

  // Abrir diálogo de confirmación para eliminar permanentemente
  const handleOpenPermanentDeleteDialog = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setDeleteConfirmText('');
    setOpenPermanentDeleteDialog(true);
  };

  const handleClosePermanentDeleteDialog = () => {
    setOpenPermanentDeleteDialog(false);
    setSelectedUsuario(null);
    setDeleteConfirmText('');
  };

  // Eliminar usuario permanentemente
  const handlePermanentDelete = async () => {
    if (!selectedUsuario || deleteConfirmText !== selectedUsuario.username) return;

    try {
      const token = localStorage.getItem('access_token');
      
      await axios.delete(
        `${API_URL}/auth/usuarios/${selectedUsuario.id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showSnackbar(
        `Usuario "${selectedUsuario.username}" eliminado permanentemente`,
        'success'
      );
      handleClosePermanentDeleteDialog();
      loadData();
    } catch (error: any) {
      console.error('Error al eliminar usuario:', error);
      const errorMsg = error.response?.data?.detail || 'Error al eliminar usuario';
      showSnackbar(errorMsg, 'error');
    }
  };

  // Desactivar o reactivar usuario (eliminación lógica)
  const handleToggleUserStatus = async () => {
    if (!selectedUsuario) return;

    const isCurrentlyActive = selectedUsuario.profile?.activo !== false && selectedUsuario.esta_activo !== false;
    const newStatus = !isCurrentlyActive;

    try {
      const token = localStorage.getItem('access_token');
      
      await axios.patch(
        `${API_URL}/auth/usuarios/${selectedUsuario.id}/`,
        {
          activo: newStatus,
          profile: {
            activo: newStatus,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      showSnackbar(
        newStatus 
          ? `Usuario "${selectedUsuario.username}" reactivado correctamente` 
          : `Usuario "${selectedUsuario.username}" desactivado correctamente`,
        'success'
      );
      handleCloseDeleteDialog();
      loadData();
    } catch (error: any) {
      console.error('Error al cambiar estado del usuario:', error);
      const errorMsg = error.response?.data?.detail || 'Error al cambiar estado del usuario';
      showSnackbar(errorMsg, 'error');
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedUsuario) return;

    try {
      const token = localStorage.getItem('access_token');
      
      // Actualizar el usuario en el backend
      await axios.patch(
        `${API_URL}/auth/usuarios/${selectedUsuario.id}/`,
        {
          username: editForm.username,
          email: editForm.email,
          profile: {
            rol: editForm.rol,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      showSnackbar('Usuario actualizado correctamente', 'success');
      handleCloseDialog();
      loadData(); // Recargar la lista
    } catch (error: any) {
      console.error('Error al actualizar usuario:', error);
      const errorMsg = error.response?.data?.detail || 'Error al actualizar usuario';
      showSnackbar(errorMsg, 'error');
    }
  };

  const getRolNombre = (usuario: Usuario): string => {
    if (usuario.profile?.rol) return usuario.profile.rol;
    if (usuario.rol) return usuario.rol;
    if (usuario.profile?.es_admin) return 'admin';
    if (usuario.profile?.es_repartidor) return 'repartidor';
    if (usuario.profile?.es_cliente) return 'cliente';
    return 'Sin rol';
  };

  const getRolColor = (usuario: Usuario): 'default' | 'error' | 'info' | 'success' => {
    const rol = getRolNombre(usuario);
    if (rol === 'admin') return 'error';
    if (rol === 'repartidor') return 'info';
    if (rol === 'cliente') return 'success';
    return 'default';
  };

  // Verificar si el usuario está activo
  const isUserActive = (usuario: Usuario): boolean => {
    // Verificar profile.activo primero, luego esta_activo
    if (usuario.profile?.activo !== undefined) return usuario.profile.activo;
    if (usuario.esta_activo !== undefined) return usuario.esta_activo;
    return true; // Por defecto activo
  };

  // Filtrar usuarios según la pestaña
  const usuariosActivos = usuarios.filter(u => isUserActive(u));
  const usuariosInactivos = usuarios.filter(u => !isUserActive(u));
  const usuariosFiltrados = tabValue === 0 ? usuariosActivos : usuariosInactivos;

  // Calcular paginación
  const totalPages = Math.ceil(usuariosFiltrados.length / ITEMS_PER_PAGE);
  const paginatedUsuarios = usuariosFiltrados.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // Resetear página cuando cambia la pestaña
  useEffect(() => {
    setCurrentPage(1);
  }, [tabValue]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Gestión de Usuarios
      </Typography>

      {/* Pestañas para filtrar activos/inactivos */}
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab 
            icon={<PersonIcon />} 
            iconPosition="start" 
            label={`Activos (${usuariosActivos.length})`} 
          />
          <Tab 
            icon={<PersonOffIcon />} 
            iconPosition="start" 
            label={`Inactivos (${usuariosInactivos.length})`} 
            sx={{ color: usuariosInactivos.length > 0 ? 'warning.main' : 'inherit' }}
          />
        </Tabs>
      </Paper>

      {/* Vista Responsiva */}
      {isMobile ? (
        // Vista Móvil - Tarjetas con acordeón
        <Box>
          {usuariosFiltrados.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 6,
                background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
                borderRadius: '16px',
                border: '2px dashed rgba(46, 90, 51, 0.2)',
              }}
            >
              <PersonIcon sx={{ fontSize: 64, color: 'rgba(46, 90, 51, 0.3)', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                {tabValue === 0 ? 'No hay usuarios activos' : 'No hay usuarios inactivos'}
              </Typography>
            </Box>
          ) : (
            paginatedUsuarios.map((usuario) => (
              <MobileCard key={usuario.id}>
                <CardHeader onClick={() => setExpandedCard(expandedCard === usuario.id ? null : usuario.id)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: isUserActive(usuario) ? '#2e5a33' : '#9e9e9e',
                        width: 48,
                        height: 48,
                      }}
                    >
                      {usuario.username.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight={600} color="#2e5a33">
                        {usuario.username}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {usuario.email}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip label={getRolNombre(usuario)} color={getRolColor(usuario)} size="small" />
                        <Chip
                          label={isUserActive(usuario) ? 'Activo' : 'Inactivo'}
                          color={isUserActive(usuario) ? 'success' : 'default'}
                          size="small"
                          variant={isUserActive(usuario) ? 'filled' : 'outlined'}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <ExpandIcon expanded={expandedCard === usuario.id}>
                    <ExpandMoreIcon fontSize="small" />
                  </ExpandIcon>
                </CardHeader>
                
                <CardContent expanded={expandedCard === usuario.id}>
                  <DetailItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BadgeIcon sx={{ color: '#2e5a33', fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>ID</Typography>
                    </Box>
                    <Typography fontWeight={500}>#{usuario.id}</Typography>
                  </DetailItem>
                  
                  {usuario.first_name && usuario.last_name && (
                    <DetailItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#2e5a33', fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Nombre</Typography>
                      </Box>
                      <Typography fontWeight={500}>{usuario.first_name} {usuario.last_name}</Typography>
                    </DetailItem>
                  )}
                  
                  <DetailItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ color: '#2e5a33', fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>Registro</Typography>
                    </Box>
                    <Typography fontWeight={500}>
                      {usuario.fecha_registro ? new Date(usuario.fecha_registro).toLocaleDateString() : '-'}
                    </Typography>
                  </DetailItem>
                  
                  <ActionsContainer>
                    <Tooltip title="Editar usuario">
                      <IconButton
                        sx={{ 
                          bgcolor: 'primary.light', 
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.main' }
                        }}
                        onClick={() => handleOpenDialog(usuario)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {isUserActive(usuario) ? (
                      <Tooltip title="Desactivar usuario">
                        <IconButton
                          sx={{ 
                            bgcolor: 'warning.light', 
                            color: 'white',
                            '&:hover': { bgcolor: 'warning.main' }
                          }}
                          onClick={() => handleOpenDeleteDialog(usuario)}
                        >
                          <PersonOffIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reactivar usuario">
                        <IconButton
                          sx={{ 
                            bgcolor: 'success.light', 
                            color: 'white',
                            '&:hover': { bgcolor: 'success.main' }
                          }}
                          onClick={() => handleOpenDeleteDialog(usuario)}
                        >
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Eliminar permanentemente">
                      <IconButton
                        sx={{ 
                          bgcolor: 'error.light', 
                          color: 'white',
                          '&:hover': { bgcolor: 'error.main' }
                        }}
                        onClick={() => handleOpenPermanentDeleteDialog(usuario)}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Tooltip>
                  </ActionsContainer>
                </CardContent>
              </MobileCard>
            ))
          )}
        </Box>
      ) : (
        // Vista Desktop - Tabla tradicional
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Fecha Registro</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuariosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      {tabValue === 0 ? 'No hay usuarios activos' : 'No hay usuarios inactivos'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsuarios.map((usuario) => (
                  <TableRow 
                    key={usuario.id}
                    sx={{ 
                      opacity: isUserActive(usuario) ? 1 : 0.7,
                      backgroundColor: isUserActive(usuario) ? 'inherit' : 'action.hover',
                    }}
                  >
                    <TableCell>{usuario.id}</TableCell>
                    <TableCell>
                      {usuario.username}
                      {usuario.first_name && usuario.last_name && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {usuario.first_name} {usuario.last_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{usuario.email}</TableCell>
                    <TableCell>
                      <Chip label={getRolNombre(usuario)} color={getRolColor(usuario)} size="small" />
                    </TableCell>
                    <TableCell>
                      {usuario.fecha_registro ? new Date(usuario.fecha_registro).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={isUserActive(usuario) ? 'Activo' : 'Inactivo'}
                        color={isUserActive(usuario) ? 'success' : 'default'}
                        size="small"
                        variant={isUserActive(usuario) ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar usuario">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(usuario)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {isUserActive(usuario) ? (
                        <Tooltip title="Desactivar usuario">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleOpenDeleteDialog(usuario)}
                          >
                            <PersonOffIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Reactivar usuario">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleOpenDeleteDialog(usuario)}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Eliminar permanentemente">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenPermanentDeleteDialog(usuario)}
                        >
                          <DeleteForeverIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginación */}
      {usuariosFiltrados.length > ITEMS_PER_PAGE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, usuariosFiltrados.length)} de {usuariosFiltrados.length} usuarios
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontWeight: 500,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                  color: 'white',
                },
              },
            }}
          />
        </Box>
      )}

      {/* Dialog para editar usuario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Usuario</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nombre de usuario"
              value={editForm.username}
              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Correo electrónico"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Rol</InputLabel>
              <Select
                value={editForm.rol}
                label="Rol"
                onChange={(e) => setEditForm({ ...editForm, rol: e.target.value })}
              >
                <MenuItem value="admin">Administrador</MenuItem>
                <MenuItem value="cliente">Cliente</MenuItem>
                <MenuItem value="repartidor">Repartidor</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveChanges} variant="contained" color="primary">
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para desactivar/reactivar usuario */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: selectedUsuario && isUserActive(selectedUsuario) ? 'warning.light' : 'success.light',
          color: 'white'
        }}>
          {selectedUsuario && isUserActive(selectedUsuario) 
            ? '⚠️ Desactivar Usuario' 
            : '✅ Reactivar Usuario'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            {selectedUsuario && isUserActive(selectedUsuario) ? (
              <>
                ¿Estás seguro de que deseas <strong>desactivar</strong> al usuario{' '}
                <strong>"{selectedUsuario?.username}"</strong>?
                <Box component="ul" sx={{ mt: 2, pl: 2 }}>
                  <li>El usuario no podrá iniciar sesión</li>
                  <li>No podrá realizar compras ni acceder a su cuenta</li>
                  <li>Sus datos se conservarán y podrás reactivarlo cuando quieras</li>
                </Box>
              </>
            ) : (
              <>
                ¿Deseas <strong>reactivar</strong> al usuario{' '}
                <strong>"{selectedUsuario?.username}"</strong>?
                <Box component="ul" sx={{ mt: 2, pl: 2 }}>
                  <li>El usuario podrá volver a iniciar sesión</li>
                  <li>Recuperará acceso a su cuenta y funcionalidades</li>
                </Box>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} variant="outlined">
            Cancelar
          </Button>
          <Button 
            onClick={handleToggleUserStatus} 
            variant="contained" 
            color={selectedUsuario && isUserActive(selectedUsuario) ? 'warning' : 'success'}
            startIcon={selectedUsuario && isUserActive(selectedUsuario) ? <PersonOffIcon /> : <RestoreIcon />}
          >
            {selectedUsuario && isUserActive(selectedUsuario) ? 'Desactivar' : 'Reactivar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para eliminar permanentemente */}
      <Dialog open={openPermanentDeleteDialog} onClose={handleClosePermanentDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'error.main', color: 'white' }}>
          🗑️ Eliminar Usuario Permanentemente
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            <Box sx={{ mb: 2 }}>
              <strong style={{ color: 'red' }}>⚠️ ADVERTENCIA: Esta acción es irreversible.</strong>
            </Box>
            Estás a punto de eliminar permanentemente al usuario{' '}
            <strong>"{selectedUsuario?.username}"</strong>.
            <Box component="ul" sx={{ mt: 2, pl: 2 }}>
              <li>Se eliminarán todos los datos del usuario</li>
              <li>Se eliminarán sus pedidos y registros asociados</li>
              <li><strong>No se podrá recuperar esta información</strong></li>
            </Box>
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              Para confirmar, escribe el nombre de usuario: <strong>{selectedUsuario?.username}</strong>
            </Box>
          </DialogContentText>
          <TextField
            fullWidth
            placeholder={`Escribe "${selectedUsuario?.username}" para confirmar`}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            sx={{ mt: 2 }}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== selectedUsuario?.username}
            helperText={
              deleteConfirmText.length > 0 && deleteConfirmText !== selectedUsuario?.username
                ? 'El nombre no coincide'
                : ''
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePermanentDeleteDialog} variant="outlined">
            Cancelar
          </Button>
          <Button 
            onClick={handlePermanentDelete} 
            variant="contained" 
            color="error"
            disabled={deleteConfirmText !== selectedUsuario?.username}
            startIcon={<DeleteForeverIcon />}
          >
            Eliminar Permanentemente
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}